// 🔧 seo_queue.js
// ✅ Purpose: Admin SEO Review Queue for event-level SEO drafts
// ✅ Features:
// - Loads events from /events
// - Shows Phase 1 SEO page targets: event_home, entries, sponsors, auction, volunteering
// - Displays each SEO page status: none, pending, approved, rejected
// - Provides placeholder buttons for Generate Draft, Approve, Reject, and Regenerate
// - Does not call OpenAI yet
// - Keeps public pages untouched while we build the admin workflow
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { getEventSeoPageKeys } from "../../utils/seo_page_defaults";

const SEO_QUEUE_PAGE_SIZE = 10;
const SITEMAP_INDEX_PATH = "/sitemap.xml";
const LOCAL_FUNCTIONS_ORIGIN = "http://127.0.0.1:5001";
const LOCAL_FUNCTIONS_PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || "let-s-go-vote---dev";
const useLocalSitemapFunctions = process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true" ||
  (process.env.REACT_APP_RUNTIME || process.env.REACT_APP_ENV) === "mission-control";

const sitemapRequestUrl = (path, refreshedAt) => {
  if (!useLocalSitemapFunctions) return `${path}?refresh=${refreshedAt}`;

  const functionPath = path === SITEMAP_INDEX_PATH
    ? "sitemapIndex"
    : path === "/sitemap-static.xml"
      ? "sitemapStatic"
      : `sitemapShard${path}`;
  return `${LOCAL_FUNCTIONS_ORIGIN}/${LOCAL_FUNCTIONS_PROJECT_ID}/us-central1/${functionPath}?refresh=${refreshedAt}`;
};

const sitemapPathsFromIndex = (xml) => Array.from(
  String(xml || "").matchAll(/<loc>([^<]+)<\/loc>/g),
  (match) => new URL(match[1], window.location.origin).pathname
);

const isSitemapXml = (xml) => {
  const document = String(xml || "").trim();
  return document.startsWith("<?xml") &&
    (document.includes("<sitemapindex") || document.includes("<urlset"));
};

const SeoQueue = () => {

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [selectedPreview, setSelectedPreview] = useState(null);
    const [generatingKeys, setGeneratingKeys] = useState([]);
    const [eventSearch, setEventSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [draftForm, setDraftForm] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [viewMode, setViewMode] = useState("event");
    const [siteSeoPages, setSiteSeoPages] = useState([]);
    const [activeEventId] = useState(() => {
      try { return JSON.parse(localStorage.getItem("LGV_RECENT_EVENT") || "{}").id || ""; } catch { return ""; }
    });
    const [sitemaps, setSitemaps] = useState({});
    const [sitemapLoading, setSitemapLoading] = useState(false);
    const [showSitemaps, setShowSitemaps] = useState(false);
    const [sitemapSource, setSitemapSource] = useState(null);

    const refreshSitemaps = async () => {
      setSitemapLoading(true);
      try {
        const rebuilt = await httpsCallable(functions, "rebuildSitemapProjections", { timeout: 540000 })();
        setSitemapSource(rebuilt.data);
        const refreshedAt = Date.now();
        const indexResponse = await fetch(sitemapRequestUrl(SITEMAP_INDEX_PATH, refreshedAt), { cache: "no-store" });
        const indexXml = await indexResponse.text();
        if (!indexResponse.ok || !isSitemapXml(indexXml)) {
          throw new Error("Sitemap endpoint returned application HTML instead of XML. Start Firebase Hosting/Functions emulators or deploy the sitemap rewrites.");
        }
        const paths = [SITEMAP_INDEX_PATH, ...sitemapPathsFromIndex(indexXml)];
        const results = await Promise.all(paths.map(async (path) => {
          if (path === SITEMAP_INDEX_PATH) return [path, { xml: indexXml, status: indexResponse.status, count: sitemapPathsFromIndex(indexXml).length, endpoint: sitemapRequestUrl(path, refreshedAt), refreshedAt: new Date().toLocaleString() }];
          const response = await fetch(sitemapRequestUrl(path, refreshedAt), { cache: "no-store" });
          const xml = await response.text();
          if (!response.ok || !isSitemapXml(xml)) {
            throw new Error(`${path} did not return sitemap XML.`);
          }
          return [path, { xml, status: response.status, count: (xml.match(/<url>/g) || []).length, endpoint: sitemapRequestUrl(path, refreshedAt), refreshedAt: new Date().toLocaleString() }];
        }));
        setSitemaps(Object.fromEntries(results));
      } catch (err) {
        logger.error("Unable to refresh sitemap XML", { value: err });
        setStatus({ type: "error", message: err?.message || "Unable to refresh sitemap XML." });
      } finally { setSitemapLoading(false); }
    };

    // ✅ Load events once when page opens.
    // ✅ This is admin-only usage, so read cost is acceptable.
    const loadEvents = async () => {
      try {
        setLoading(true);

        const eventsQuery = query(
        collection(db, "events"),
        orderBy("startDate", "asc")
        );

        const snap = await getDocs(eventsQuery);

        const { startDate, endDate } = getSeoQueueDateRangeForJs();

        const eventList = snap.docs
        .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
        }))
        .filter((event) => {
            const eventDate = normalizeEventDate(event.startDate);

            if (!eventDate) {
            return false;
            }

            const inSeoWindow =
            eventDate >= startDate &&
            eventDate <= endDate;

            if (!inSeoWindow) {
            return false;
            }

            return true;
        });

        setEvents(eventList);

        logger.log("🔎 SEO Queue events loaded", {
          count: eventList.length,
        });
      } catch (err) {
        logger.error("❌ Error loading SEO Queue events:", {
          value: err,
        });
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
        loadEvents();
        const loadSiteSeo = async () => {
          try {
            await httpsCallable(functions, "ensureSiteSeoPages")();
            const snap = await getDocs(collection(db, "site_seo_pages"));
            setSiteSeoPages(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
          } catch (err) { logger.error("Unable to load site SEO records", { value: err }); }
        };
        loadSiteSeo();
    }, []);

  // ✅ Converts Firestore Timestamp or date string into readable date text.
  const formatEventDate = (value) => {
    if (!value) return "No date";

    const date = value.toDate ? value.toDate() : new Date(value);

    if (Number.isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString();
  };

  // ✅ Formats SEO timestamp fields for review queue display.
    const formatSeoDate = (value) => {
        if (!value) return "";

        const date = value.toDate ? value.toDate() : new Date(value);

        if (Number.isNaN(date.getTime())) return "";

        return date.toLocaleString();
    };

    // ✅ Returns JS Date range for local filtering.
    // ✅ Safer than Firestore where filters while we still may have mixed date formats.
    const getSeoQueueDateRangeForJs = () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 60);
    endDate.setHours(23, 59, 59, 999);

    return {
        startDate,
        endDate,
    };
    };

    // ✅ Normalizes Firestore Timestamp, JS Date, or date string.
    const normalizeEventDate = (value) => {
    if (!value) return null;

    if (value.toDate) {
        return value.toDate();
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
    };

    // ✅ Returns the draft object for one SEO page.
    // ✅ Safely supports older events that do not have seoPages yet.
    const getSeoPage = (event, pageKey) => {
        return event?.seoPages?.[pageKey] || {};
    };

    const getQueueStatus = (seoPage) =>
      seoPage?.status === "approved" ? "approved" : "draft";

    const openPreview = (event, pageKey, sitewide = false, suppliedPage = null) => {
      const seoPage = suppliedPage || getSeoPage(event, pageKey);
      setSelectedPreview({ event, pageKey, sitewide, siteRecordId: seoPage.id });
      setDraftForm({
        title: seoPage.title || "",
        description: seoPage.description || "",
        suggestedOgTitle: seoPage.suggestedOgTitle || "",
        suggestedOgDescription: seoPage.suggestedOgDescription || "",
        keywords: Array.isArray(seoPage.keywords) ? seoPage.keywords.join(", ") : "",
      });
    };

    // ✅ Calls Cloud Function SEO Agent.
    // ✅ OpenAI generates draft content.
    // ✅ Function writes draft into Firestore.
    // ✅ Public pages still require approval.
    const handleGenerateDraft = async (event, pageKey, sitewide = false, siteRecordId = "") => {
    if (!event?.id || !pageKey) return;

    const generationKey = sitewide ? `sitewide:${siteRecordId}` : `${event.id}:${pageKey}`;
    if (generatingKeys.includes(generationKey)) return;

    try {
        setGeneratingKeys((keys) => [...keys, generationKey]);
        setStatus({
        type: "info",
        message: `Generating SEO content for ${sitewide ? "Sitewide" : event.name} / ${pageKey}...`,
        });

        const result = sitewide
          ? await httpsCallable(functions, "generateSiteSeoDraft")({ path: pageKey })
          : await httpsCallable(functions, "generateEventSeoDraft")({ eventId: event.id, pageKey });
        const draft = result?.data?.draft || {};
        setDraftForm({
          title: draft.title || "",
          description: draft.description || "",
          suggestedOgTitle: draft.suggestedOgTitle || "",
          suggestedOgDescription: draft.suggestedOgDescription || "",
          keywords: Array.isArray(draft.keywords) ? draft.keywords.join(", ") : "",
        });
        if (sitewide) {
          setSiteSeoPages((current) => current.map((item) => item.id === siteRecordId ? { ...item, ...draft, status: "pending" } : item));
        } else setEvents((current) => current.map((item) => item.id === event.id ? {
          ...item,
          seoPages: { ...item.seoPages, [pageKey]: { ...item.seoPages?.[pageKey], ...draft, status: "pending" } },
        } : item));

        setStatus({
        type: "success",
        message: `SEO content generated for ${sitewide ? "Sitewide" : event.name} / ${pageKey}.`,
        });

        if (!sitewide) await loadEvents();
    } catch (err) {
        logger.error("❌ SEO Agent failed:", {
        value: err,
        });

        setStatus({
        type: "error",
        message:
            err?.message ||
            "SEO draft generation failed.",
        });
    } finally {
        setGeneratingKeys((keys) =>
          keys.filter((key) => key !== generationKey)
        );
    }
    };

    // ✅ Approves a generated SEO draft.
    // ✅ Later public pages will only use approved SEO.
    const handleApproveDraft = async () => {
    const event = selectedPreview?.event;
    const pageKey = selectedPreview?.pageKey;
    if (!event?.id || !pageKey || !draftForm?.title.trim() || !draftForm?.description.trim()) {
      setStatus({ type: "error", message: "SEO Title and SEO Description are required before approval." });
      return;
    }
    try {
        if (selectedPreview.sitewide) {
          await updateDoc(doc(db, "site_seo_pages", selectedPreview.siteRecordId), {
            title: draftForm.title.trim(), description: draftForm.description.trim(),
            suggestedOgTitle: draftForm.suggestedOgTitle.trim(), suggestedOgDescription: draftForm.suggestedOgDescription.trim(),
            keywords: draftForm.keywords.split(",").map((value) => value.trim()).filter(Boolean), status: "approved", updatedAt: serverTimestamp(),
          });
          setSiteSeoPages((current) => current.map((item) => item.id === selectedPreview.siteRecordId ? { ...item, ...draftForm, status: "approved" } : item));
          setStatus({ type: "success", message: `${selectedPreview.pageKey} approved.` });
          return;
        }
        await updateDoc(doc(db, "events", event.id), {
        [`seoPages.${pageKey}.title`]: draftForm.title.trim(),
        [`seoPages.${pageKey}.description`]: draftForm.description.trim(),
        [`seoPages.${pageKey}.suggestedOgTitle`]: draftForm.suggestedOgTitle.trim(),
        [`seoPages.${pageKey}.suggestedOgDescription`]: draftForm.suggestedOgDescription.trim(),
        [`seoPages.${pageKey}.keywords`]: draftForm.keywords.split(",").map((value) => value.trim()).filter(Boolean),
        [`seoPages.${pageKey}.status`]: "approved",
        [`seoPages.${pageKey}.approvedAt`]: serverTimestamp(),
        [`seoPages.${pageKey}.approvedBy`]: "admin",
        });

        setEvents((current) => current.map((item) => item.id === event.id ? {
          ...item,
          seoPages: { ...item.seoPages, [pageKey]: { ...item.seoPages?.[pageKey], ...draftForm, keywords: draftForm.keywords.split(",").map((value) => value.trim()).filter(Boolean), status: "approved" } },
        } : item));

        setStatus({
        type: "success",
        message: `${event.name} / ${pageKey} approved.`,
        });

        await loadEvents();
    } catch (err) {
        logger.error("❌ Error approving SEO draft:", {
        value: err,
        });

        setStatus({
        type: "error",
        message: "Failed to approve SEO draft.",
        });
    }
    };

    const queueRows = useMemo(() => {
      const searchTerm = eventSearch.trim().toLowerCase();

      if (viewMode === "sitewide") return siteSeoPages
        .filter((page) => `${page.path || ""} ${page.title || ""}`.toLowerCase().includes(searchTerm))
        .map((seoPage) => ({ event: { id: "sitewide", name: "Sitewide", slug: seoPage.path, startDate: null }, pageKey: seoPage.path, seoPage, sitewide: true }))
        .filter(({ seoPage }) => statusFilter === "all" || getQueueStatus(seoPage) === statusFilter);
      return events.filter((event) => !activeEventId || event.id === activeEventId).flatMap((event) => {
        const matchesSearch = `${event.name || ""} ${event.slug || ""}`
          .toLowerCase()
          .includes(searchTerm);

        if (!matchesSearch) return [];

        return getEventSeoPageKeys(event)
          .filter((pageKey) => event.seoPages?.[pageKey])
          .map((pageKey) => ({
            event,
            pageKey,
            seoPage: getSeoPage(event, pageKey),
          }))
          .filter(({ seoPage }) =>
            statusFilter === "all" || getQueueStatus(seoPage) === statusFilter
          );
      });
    }, [events, eventSearch, statusFilter, viewMode, siteSeoPages, activeEventId]);

    const totalPages = Math.max(
      1,
      Math.ceil(queueRows.length / SEO_QUEUE_PAGE_SIZE)
    );
    const activePage = Math.min(currentPage, totalPages);
    const visibleRows = queueRows.slice(
      (activePage - 1) * SEO_QUEUE_PAGE_SIZE,
      activePage * SEO_QUEUE_PAGE_SIZE
    );


  if (loading) {
    return <FullPageSpinner message="Loading SEO Queue..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🔎 SEO Queue</h1>
          <p className="lead text-muted">
            Review and manage AI-assisted SEO drafts for event pages.
          </p>
        </div>
      </div>

        <div className="white-section p-4 rounded shadow-sm bg-white">
        {status?.message && (
            <div
            className={`alert ${
                status.type === "success"
                ? "alert-success"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            >
            {status.message}
            </div>
        )}

        {!showSitemaps && <div className="alert alert-info">
              {viewMode === "sitewide" ? "Review editable sitewide SEO, including Power Up marketing pages." : activeEventId ? "Showing SEO records for the currently viewed event." : "No current event is selected; choose Sitewide or open an event first."}
        </div>}
        {!showSitemaps && <div className="btn-group mb-3" role="group" aria-label="SEO scope">
          <button type="button" className={`btn ${viewMode === "event" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => { setViewMode("event"); setCurrentPage(1); }}>Current Event</button>
          <button type="button" className={`btn ${viewMode === "sitewide" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => { setViewMode("sitewide"); setCurrentPage(1); }}>Sitewide Pages</button>
        </div>}
        <div className="mb-3">
          <button type="button" className="btn btn-outline-primary" onClick={() => { setShowSitemaps(true); if (Object.keys(sitemaps).length === 0) refreshSitemaps(); }}>Show Sitemap XML</button>
          {showSitemaps && <button type="button" className="btn btn-outline-secondary ms-2" onClick={() => setShowSitemaps(false)}>Show SEO Records</button>}
        </div>
        {showSitemaps && <div className="card border-0 shadow-sm rounded-3 mb-4">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <strong>Global Sitemap XML</strong>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={refreshSitemaps} disabled={sitemapLoading}>{sitemapLoading ? "Refreshing..." : "Refresh XML"}</button>
          </div>
          <div className="card-body">
            <p className="small text-muted">Refresh XML rebuilds public URLs across all events and enabled Power Ups in the connected Firebase project, then loads every file listed in the index.</p>
            <div className="alert alert-info">
              Data source: {useLocalSitemapFunctions ? "Mission Control local emulator" : "Connected cloud project"} ({sitemapSource?.projectId || LOCAL_FUNCTIONS_PROJECT_ID}).
              {sitemapSource && <> Events scanned: {sitemapSource.eventsSynced}.</>}
              {useLocalSitemapFunctions && <> Local records can include test or imported data. Production-domain URLs are canonical links; they do not indicate a production data read.</>}
            </div>
            <div className="accordion" id="sitemap-xml-accordion">
              {Object.keys(sitemaps).map((path, index) => {
                const sitemap = sitemaps[path];
                return <div className="accordion-item" key={path}>
                  <h2 className="accordion-header" id={`sitemap-heading-${index}`}><button className={`accordion-button ${index ? "collapsed" : ""}`} type="button" data-bs-toggle="collapse" data-bs-target={`#sitemap-collapse-${index}`}>{path} {sitemap && <small className="ms-2 text-muted">HTTP {sitemap.status} · {sitemap.refreshedAt}</small>}</button></h2>
                  <div id={`sitemap-collapse-${index}`} className={`accordion-collapse collapse ${index ? "" : "show"}`}><div className="accordion-body"><p className="small text-muted text-break">{sitemap?.endpoint} · {sitemap?.count || 0} {path === SITEMAP_INDEX_PATH ? "sitemap files" : "URLs"}</p><pre className="small mb-0 text-wrap">{sitemap?.xml || "Load pending."}</pre></div></div>
                </div>;
              })}
            </div>
          </div>
        </div>}

        {!showSitemaps && selectedPreview && (
        <div className="card border-dark mb-4">
            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
            <div>
                🔍 SEO Preview: {selectedPreview.event?.name || "Event"} /{" "}
                {selectedPreview.pageKey}
            </div>

            <button
                type="button"
                className="btn btn-light btn-sm"
                onClick={() => setSelectedPreview(null)}
            >
                Close
            </button>
            </div>

            <div className="card-body">
            <div className="mb-3">
                <strong>Status:</strong>{" "}
                <span className={`badge ${getQueueStatus(getSeoPage(selectedPreview.event, selectedPreview.pageKey)) === "approved" ? "bg-success" : "bg-warning text-dark"}`}>
                {getQueueStatus(getSeoPage(selectedPreview.event, selectedPreview.pageKey)) === "approved" ? "Approved" : "Draft"}
                </span>
            </div>

            <div className="mb-3">
                <strong>SEO Title</strong>
                <input className="form-control" value={draftForm?.title || ""} onChange={(event) => setDraftForm((current) => ({ ...current, title: event.target.value }))} />
            </div>

            <div className="mb-3">
                <strong>SEO Description</strong>
                <textarea className="form-control" rows="3" value={draftForm?.description || ""} onChange={(event) => setDraftForm((current) => ({ ...current, description: event.target.value }))} />
            </div>

            <div className="mb-3">
                <strong>Suggested Open Graph Title</strong>
                <input className="form-control" value={draftForm?.suggestedOgTitle || ""} onChange={(event) => setDraftForm((current) => ({ ...current, suggestedOgTitle: event.target.value }))} />
            </div>

            <div className="mb-3">
                <strong>Suggested Open Graph Description</strong>
                <textarea className="form-control" rows="3" value={draftForm?.suggestedOgDescription || ""} onChange={(event) => setDraftForm((current) => ({ ...current, suggestedOgDescription: event.target.value }))} />
            </div>

            <div className="mb-3">
                <strong>Keywords / Focus Terms</strong>
                <input className="form-control" value={draftForm?.keywords || ""} onChange={(event) => setDraftForm((current) => ({ ...current, keywords: event.target.value }))} placeholder="Comma-separated terms" />
            </div>

            <div className="small text-muted">
                {selectedPreview.seoPage?.generatedAt && (
                <div>
                    Generated: {formatSeoDate(selectedPreview.seoPage.generatedAt)}
                </div>
                )}

                {selectedPreview.seoPage?.approvedAt && (
                <div>
                    Approved: {formatSeoDate(selectedPreview.seoPage.approvedAt)}
                </div>
                )}

                {selectedPreview.seoPage?.rejectedAt && (
                <div>
                    Rejected: {formatSeoDate(selectedPreview.seoPage.rejectedAt)}
                </div>
                )}
            </div>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button type="button" className="btn btn-outline-primary" disabled={generatingKeys.includes(selectedPreview.sitewide ? `sitewide:${selectedPreview.siteRecordId}` : `${selectedPreview.event.id}:${selectedPreview.pageKey}`) || getQueueStatus(getSeoPage(selectedPreview.event, selectedPreview.pageKey)) === "approved"} onClick={() => handleGenerateDraft(selectedPreview.event, selectedPreview.pageKey, selectedPreview.sitewide, selectedPreview.siteRecordId)}>
                {generatingKeys.includes(selectedPreview.sitewide ? `sitewide:${selectedPreview.siteRecordId}` : `${selectedPreview.event.id}:${selectedPreview.pageKey}`) ? "Generating..." : "Generate Content"}
              </button>
              <button type="button" className="btn btn-success" onClick={handleApproveDraft}>Approve</button>
            </div>
            </div>
        </div>
        )}

        {!showSitemaps && <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
          <div className="card-body border-bottom">
            <div className="row g-3">
              <div className="col-12 col-md-8"><input className="form-control" value={eventSearch} onChange={(event) => { setEventSearch(event.target.value); setCurrentPage(1); }} placeholder="Search event name or slug" /></div>
              <div className="col-12 col-md-4"><select className="form-select" value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setCurrentPage(1); }}><option value="all">All statuses</option><option value="draft">Draft</option><option value="approved">Approved</option></select></div>
            </div>
          </div>
        <div className="table-responsive">

          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Event</th>
                <th>Date</th>
                <th>SEO Page</th>
                <th>Status</th>
                <th>Draft Preview</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {queueRows.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-muted text-center">
                    No events found.
                  </td>
                </tr>
              ) : (
                visibleRows.map(({ event, pageKey, seoPage, sitewide }) => {
                    return (
                      <tr key={`${event.id}-${pageKey}`}>
                        <td>
                          <strong>{event.name || "Unnamed Event"}</strong>
                          <br />
                          <small className="text-muted">
                            {event.slug || event.id}
                          </small>
                        </td>

                        <td>{sitewide ? "Sitewide" : formatEventDate(event.startDate)}</td>

                        <td>
                          <code>{pageKey}</code>
                        </td>

                        <td>
                        <span
                            className={`badge ${
                                getQueueStatus(seoPage) === "approved" ? "bg-success" : "bg-warning text-dark"
                            }`}
                        >
                            {getQueueStatus(seoPage) === "approved" ? "Approved" : "Draft"}
                        </span>
                        </td>

                        <td style={{ minWidth: "360px" }}>
                        {seoPage?.title || seoPage?.description ? (
                            <div>
                            <div className="fw-bold mb-1">
                                {seoPage.title || "No title"}
                            </div>

                            <div className="small text-muted mb-2">
                                {seoPage.description || "No description"}
                            </div>

                            {seoPage.keywords?.length > 0 && (
                                <div className="mb-2">
                                {seoPage.keywords.map((keyword, keywordIndex) => (
                                    <span
                                    key={`${event.id}-${pageKey}-${keyword}-${keywordIndex}`}
                                    className="badge bg-light text-dark border me-1 mb-1"
                                    >
                                    {keyword}
                                    </span>
                                ))}
                                </div>
                            )}

                            <div className="small text-muted">
                                {seoPage.generatedAt && (
                                <div>
                                    Generated: {formatSeoDate(seoPage.generatedAt)}
                                </div>
                                )}

                                {seoPage.approvedAt && (
                                <div>
                                    Approved: {formatSeoDate(seoPage.approvedAt)}
                                </div>
                                )}

                                {seoPage.rejectedAt && (
                                <div>
                                    Rejected: {formatSeoDate(seoPage.rejectedAt)}
                                </div>
                                )}
                            </div>
                            </div>
                        ) : (
                            <span className="text-muted small">
                            No draft generated yet.
                            </span>
                        )}
                        </td>

                        <td>
                        <div className="d-flex flex-wrap gap-2">

                            <button
                            type="button"
                            className="btn btn-outline-dark btn-sm"
                            onClick={() =>
                                openPreview(event, pageKey, sitewide, seoPage)
                            }
                            >
                            Preview
                            </button>

                          </div>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
        {queueRows.length > SEO_QUEUE_PAGE_SIZE && (
          <div className="card-footer bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
            <span className="small text-muted">
              Showing {(activePage - 1) * SEO_QUEUE_PAGE_SIZE + 1}–{Math.min(activePage * SEO_QUEUE_PAGE_SIZE, queueRows.length)} of {queueRows.length} SEO records
            </span>
            <div className="btn-group" role="group" aria-label="SEO queue pagination">
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={activePage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>Previous</button>
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled>{activePage} / {totalPages}</button>
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={activePage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>Next</button>
            </div>
          </div>
        )}
        </div>}
      </div>
    </section>
  );
};

export default SeoQueue;
